import { useAuth } from '@/contexts/AuthContext';

// Roles that always have every capability — a safety net so the UI never
// hides a control from a full-access admin even if `permissions` hasn't
// loaded yet.
const FULL_ACCESS_ROLES = ['admin', 'tenant_admin', 'super_admin', 'owner'];

/**
 * usePermissions — permission-aware UI primitive.
 *
 * `can(permission)` mirrors the backend's effective-permission policy
 * (from /auth/me → user.permissions), so controls a user's role can't use
 * are hidden/disabled instead of letting them hit a 403.
 *
 * Fail-open only for full-access roles; for everyone else we require the
 * permission to be explicitly present.
 */
export function usePermissions() {
  const { user } = useAuth();
  const role = user?.role;
  const perms = user?.permissions;

  const can = (permission) => {
    if (!permission) return true;
    if (FULL_ACCESS_ROLES.includes(role)) return true;
    if (!Array.isArray(perms)) return false;
    return perms.includes(permission);
  };

  const canAny = (...permissions) => permissions.some(can);

  return { can, canAny, role, permissions: perms || [] };
}

export function useCan(permission) {
  const { can } = usePermissions();
  return can(permission);
}
